import React, { useState, useMemo } from 'react';
import {
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  ListItem,
  ListItemText,
  Modal,
  Typography,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { FixedSizeList } from 'react-window';
import dayjs from 'dayjs';
import { formatTime } from '../../../../services/util/date/formatTime';
import { Game } from '../../../../interfaces/Games.interface';
import { Streamingpackage } from '../../../../interfaces/StreamingProvider.interface';
import { StyledBox } from './BCCoveredGames.styled';

type SharedModalProps = {
  providerPackageCoverage: { [key: string]: Set<number> };
  providers: Streamingpackage[];
  bestProvider: Streamingpackage[];
  openModal: boolean;
  handleCloseModal: () => void;
  games: Game[];
  heading: string;
  type: 'COVERED' | 'UNCOVERED';
};

const SharedModal: React.FC<SharedModalProps> = ({
  openModal,
  handleCloseModal,
  games,
  heading,
  providerPackageCoverage,
  providers,
  bestProvider,
  type,
}) => {
  const [expanded, setExpanded] = useState<string | false>(false);

  //Games grouping by tournament name
  const groupedGames = useMemo(() => {
    const grouped = games.reduce(
      (acc, game) => {
        const { tournament_name } = game;
        if (!acc[tournament_name]) {
          acc[tournament_name] = [];
        }
        acc[tournament_name].push(game);
        return acc;
      },
      {} as Record<string, Game[]>
    );

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([tournament, games]) => ({
        tournament,
        games: games.sort((a, b) =>
          dayjs(a.starts_at).isBefore(dayjs(b.starts_at)) ? -1 : 1
        ),
      }));
  }, [games]);

  const getProviderNameById = (id: string): string => {
    const provider = providers.find((p) => p.id.toString() === id);
    return provider ? provider.name : '';
  };

  const getProvidersForGame = useMemo(
    () =>
      (gameId: string): { best: string[]; others: string[] } => {
        const allProviders = Object.entries(providerPackageCoverage)
          .filter(([_, gameSet]) => gameSet.has(Number(gameId)))
          .map(([providerId]) => getProviderNameById(providerId));

        const best = allProviders.filter((providerName) =>
          bestProvider.some((p) => p.name === providerName)
        );
        const others = allProviders.filter(
          (providerName) => !best.some((bestName) => bestName === providerName)
        );

        return { best, others };
      },
    [providerPackageCoverage, bestProvider, providers]
  );

  const handleAccordionToggle = (tournament: string) => {
    setExpanded((prev) => (prev === tournament ? false : tournament));
  };

  return (
    <Modal open={openModal} onClose={handleCloseModal}>
      <StyledBox sx={{ bgcolor: 'background.paper' }}>
        <Typography variant="h6" gutterBottom>
          {heading}
        </Typography>
        {type === 'COVERED' && (
          <Alert variant="filled" severity="info" color="info">
            The colored provider is a best provider, and others means that these
            providers also show this game.
          </Alert>
        )}

        {groupedGames.map(({ tournament, games }) => (
          <Accordion
            key={tournament}
            expanded={expanded === tournament}
            onChange={() => handleAccordionToggle(tournament)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${tournament}-content`}
              id={`${tournament}-header`}
            >
              <Typography fontWeight={'bold'}>{tournament}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              {/* List from react-window, => virtualized */}
              <FixedSizeList
                height={300}
                width="100%"
                itemSize={150}
                itemCount={games.length}
                itemData={games}
              >
                {({ index, style }) => {
                  const game = games[index];
                  const { best, others } = getProvidersForGame(game.id);
                  return (
                    <ListItem
                      key={game.id}
                      style={{
                        ...style, // react-window style
                        borderBottom: '1px solid #ddd',
                        padding: '16x 16px',
                      }}
                    >
                      <ListItemText
                        primary={`${game.team_home} vs. ${game.team_away}`}
                        secondary={
                          <>
                            {formatTime(game.starts_at)}
                            <br />
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              <span>
                                {best.map((name, i) => (
                                  <Typography
                                    key={i}
                                    component="span"
                                    fontWeight="bold"
                                    color="primary"
                                  >
                                    {name} &nbsp;
                                  </Typography>
                                ))}
                              </span>
                              {others.length > 0 && (
                                <>
                                  <br />
                                  <Typography variant="body2" component="span">
                                    {`Other: ${others.join(', ')}`}
                                  </Typography>
                                </>
                              )}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  );
                }}
              </FixedSizeList>
            </AccordionDetails>
          </Accordion>
        ))}

        <Button
          onClick={handleCloseModal}
          variant="contained"
          sx={{ marginTop: 2 }}
        >
          Close
        </Button>
      </StyledBox>
    </Modal>
  );
};

export default SharedModal;
